import { Box, Tabs, Tab, Button, Alert, Snackbar, useTheme, useMediaQuery, Paper, Typography } from "@mui/material";
import { colors } from "../../theme/muiColors";
import {
  BusinessCenter as BusinessCenterIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { useState, useEffect, useMemo } from "react";
import { gateway } from "../../gateway";
import { decodeToken } from "../../utils";
import { useAppSelector } from "src/redux/hooks/redux";
import { ServicesLayout } from "./Services.layout";

export const ServicesTab = ({ sx }) => {
  const [value, setValue] = useState(0);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const token = sessionStorage.getItem("token");
  const decode = decodeToken(token);

  const { currentCompany } = useAppSelector((state) => state.userState)
  const companyId = currentCompany?.uid;




  const [isLoading, setIsLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const [residentialServices, setResidentialServices] = useState([]);
  const [commercialServices, setCommercialServices] = useState([]);

  const [resPayload, setResPayload] = useState({ services: [] });
  const [comPayload, setComPayload] = useState({ services: [] });

  const combinedPayload = useMemo(() => {
    const map = new Map();
    (resPayload.services || []).forEach((s) => map.set(s.id, s.isActive));
    (comPayload.services || []).forEach((s) => map.set(s.id, s.isActive));
    return { services: Array.from(map, ([id, isActive]) => ({ id, isActive })) };
  }, [resPayload, comPayload]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await gateway.get(`/workservices/self`, {
          headers: { Authorization: token },
        });
        const list = data?.data ?? [];
        setResidentialServices(list.filter((s) => (s?.workTypes?.type || "").toLowerCase().startsWith("resid")));
        setCommercialServices(list.filter((s) => (s?.workTypes?.type || "").toLowerCase().startsWith("commerc")));
      } catch {
        setAlertMessage("Error loading services");
        setAlertSeverity("error");
        setOpenAlert(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [token]);

  const handleSave = async () => {
    try {
      if (combinedPayload.services.length === 0) {
        setAlertMessage("No changes to save");
        setAlertSeverity("info");
        setOpenAlert(true);
        return;
      }
      setIsLoading(true);
      await gateway.put(
        "/workservices/self",
        { companyId: null, services: combinedPayload.services },
        { headers: { Authorization: token } }
      );
      setAlertMessage("Changes saved");
      setAlertSeverity("success");
      setOpenAlert(true);
    } catch {
      setAlertMessage("Error saving changes");
      setAlertSeverity("error");
      setOpenAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        minHeight: "500px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        ...sx,
      }}
    >
      <Box sx={{ maxWidth: 1200, width: "100%", mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 4, pt: 3, backgroundColor: "var(--fx-surface-base, #FFFFFF)" }}>
            <Tabs
              value={value}
              onChange={(_, v) => setValue(v)}
              variant={isSmall ? "scrollable" : "fullWidth"}
              scrollButtons={isSmall ? "auto" : false}
              allowScrollButtonsMobile
              textColor="primary"
              indicatorColor="primary"
              TabIndicatorProps={{ style: { height: 3, borderRadius: "3px 3px 0 0" } }}
              sx={{
                "& .MuiTab-root": {
                  minHeight: 60,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                  px: 3,
                  py: 2,
                  borderRadius: "8px 8px 0 0",
                },
              }}
            >
              <Tab
                icon={<HomeIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Residential Services"
              />
              <Tab
                icon={<BusinessCenterIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Commercial Services"
              />
            </Tabs>
          </Box>

          <Box sx={{ backgroundColor: "var(--fx-surface-base, #FFFFFF)" }}>
            {value === 0 && (
              <ServicesLayout
                services={residentialServices}
                onChangedServicesChange={setResPayload}
                title="Residential Services"
                isLoading={isLoading}
              />
            )}
            {value === 1 && (
              <ServicesLayout
                services={commercialServices}
                onChangedServicesChange={setComPayload}
                title="Commercial Services"
                isLoading={isLoading}
              />
            )}
          </Box>

          <Box
            sx={{
              p: 4,
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
              textAlign: "center",
              display: "flex",
              gap: 2,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading}
              sx={{
                px: 6,
                py: 1.5,
                minWidth: isSmall ? "100%" : 220,
                borderRadius: 32,
                backgroundColor: colors.blue[400],
                "&:hover": { backgroundColor: colors.blue[600] },
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Save Changes
            </Button>
            <Typography variant="body2" color="text.secondary">
              {combinedPayload.services.length} service
              {combinedPayload.services.length !== 1 ? "s" : ""} in payload
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={openAlert}
        autoHideDuration={4000}
        onClose={() => setOpenAlert(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenAlert(false)}
          severity={alertSeverity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
